import { DateTime } from 'luxon';
import { createSelector } from 'reselect';
import { achievementsSelectors } from '../achievements';
import { MILESTONE_REDUCER_KEY } from './milestonesReducer';

export const getResourceStore = (state) => state[MILESTONE_REDUCER_KEY];
export const getLastSyncDate = (state) => getResourceStore(state).lastSyncDate;
export const getMilestonesById = (state) => getResourceStore(state)?.resourcesById;
export const getMilestoneById = (state, id) => getResourceStore(state).resourcesById.get(id);
export const getLoadPending = (state) => getResourceStore(state).loadPending;
export const getIsLoading = (state) => getResourceStore(state).isLoading;

export const getSortedMilestones = createSelector(
  (state) => getMilestonesById(state),
  (milestones) => milestones.toList().sort((a, b) => (a.targetCount < b.targetCount ? -1 : 1)),
);

export const getMilestonesByAchievementId = createSelector(getMilestonesById, (achievements) => {
  // group milestones into 'achievements' groups
  const sortedAchievements = achievements?.toList()?.groupBy((x) => x.achievementId);
  // sort the milestones in the resulting ordered map
  return sortedAchievements?.withMutations((achievement) => {
    achievement.forEach((row, key) =>
      achievement.set(
        key,
        row.sort((a, b) => (Number(a.knownMilestoneItemId) < Number(b.knownMilestoneItemId) ? -1 : 1)),
      ),
    );
  });
});

export const isAchievementCompletedOrStarted = createSelector(
  getMilestonesByAchievementId,
  (_, props) => props.achievementId,
  (achievements, id) => {
    const milestoneList = achievements?.get(id);
    let count = 0;
    milestoneList.forEach((milestone) => {
      if (milestone?.status === 'COMPLETED') {
        count += 1;
      }
    });
    return {
      isCompleted: count >= milestoneList?.size,
      justStarted: count === 1,
    };
  },
);

export const getUpNextMilestones = createSelector(
  achievementsSelectors.getAchievementsBySortId,
  getMilestonesByAchievementId,
  (sortOrder, achievementsMap) => {
    const recentlyEarned = [];
    const milestoneArr = [];
    let count = 0;
    let total = 0;
    sortOrder?.forEach((achievement) => {
      const milestoneRow = achievementsMap?.get(achievement?.id);
      let hasIterated = false;
      milestoneRow?.forEach((milestone) => {
        total += 1;
        if (milestone?.completedOn) {
          count += 1;
          const completedOn = DateTime.fromISO(milestone?.completedOn);
          if (completedOn) {
            const modifiedAt = DateTime.fromISO(milestone?.modifiedAt);
            const index = recentlyEarned.findIndex((item) => DateTime.fromISO(item?.modifiedAt) < modifiedAt);
            recentlyEarned.splice(index, 0, milestone);
          }
        }
        if (milestone?.type === 'RANDOM') {
          hasIterated = true;
        }
        if (milestone?.status === 'IN_PROGRESS' && !hasIterated) {
          const milestonePercentage =
            (milestone?.currentAmount || milestone?.currentCount) / (milestone?.targetAmount || milestone?.targetCount);
          const index = milestoneArr.findIndex(
            (mItem) =>
              (mItem?.milestone?.currentAmount || mItem?.milestone?.currentCount) /
                (mItem?.milestone?.targetAmount || mItem?.milestone?.targetCount) <
              milestonePercentage,
          );
          if (index === -1) {
            milestoneArr.push({ milestone });
          } else {
            milestoneArr.splice(index, 0, { milestone });
          }
          hasIterated = true;
        }
        if (milestone?.status === 'NOT_STARTED' && !hasIterated) {
          milestoneArr.push({ milestone });
          hasIterated = true;
        }
        return true;
      });
    });
    return { milestoneArr, recentlyEarned, count, total };
  },
);
